import type {
  AddAndSendSupplementaryFileSchema,
  AllStageSupplierUserListItem,
  ApiResponse,
  AuditSupplierFileSchema,
  CreateSupplierMaterialSchema,
  SavePerformanceEvaluationSchema,
  PageResponse,
  PerformanceEvaluationListItem,
  PerformanceEvaluationListParams,
  PurchasePerformanceEvaluationIndicator,
  QuerySupplierMaterialSchema,
  QuerySupplierUserAllStageSchema,
  SaveOrUpdateSupplierPerformanceEvaluationIndicatorSchema,
  SaveSupplementaryFileSchema,
  SupplierFileListItem,
  SupplierMaterialListItem,
  SupplierRelationListItem,
  SupplierUserAccountListItem,
  SupplierUserSimpleListItem,
  SupplierUserSimpleListParams,
  UpdateSupplementaryFileSchema,
  UpdateSupplierMaterialSchema,
  PerformanceEvaluationDetail,
  UpdatePerformanceEvaluationAdviceSchema,
  QueryNoticePageList,
  NoticeInfoListItem,
  SupplierBlockListItem,
  SupplierBlacklistItem,
  SupplierBlacklistItemDetail,
  SupplierBlockItemDetail,
  SupplierCodeMaterialListItem,
  SupplierAccountAuditParams,
} from "@/types/apis";
import request from "@/utils/request";

/**
 * 供应商注册分页列表
 * @param data 请求参数
 */
export const registrationPage = (data: any) =>
  request.post("/purchase/supplierRegister/registrationPage", data);

/**
 * 获取邮件邀请模板
 */
export const getInviteEmailTemplate = () =>
  request.get("/purchase/supplierRegister/inviteEmailTemplate");

/**
 * 发送邮件邀请供应商
 * @param data 请求参数
 */
export const inviteSupplier = (data: {
  supplierName: string;
  email: string;
  emailSubject: string;
  emailBody: string;
}) => request.post("/purchase/supplierRegister/inviteSupplier", data);

/**
 * 获取准入评估模板树形结构
 */
export const getAdmissionEvaluationCategoryTree = () =>
  request.get("/evaluationCategory/tree");

/**
 * 保存准入评估模板树形结构
 * @param data 树形结构数据
 */
export const saveAdmissionEvaluationCategoryTree = (data: any) =>
  request.post("/evaluationCategory/saveTree", data);

/**
 * 获取所有部门下拉列表数据
 */
export const getDepartmentList = () => request.post("/purchase/dept/list", {});

/**
 * 获取用户列表（用于搜索申请人）
 * @param data 请求参数，包含 nickName 模糊查询条件
 */
export const getUserList = (data: { nickName: string }) =>
  request.post("/purchase/user/page", data);

/**
 * 供应商绩效考核信息列表
 * @param data
 */
export const getPerformanceEvaluationList = (
  data: PerformanceEvaluationListParams,
) =>
  request.post<PageResponse<PerformanceEvaluationListItem>>(
    "/purchase/performance/evaluation/page",
    data,
  );

/**
 * 获取供应商简单信息列表
 * @param data
 */
export const getSupplierUserSimpleList = (data: SupplierUserSimpleListParams) =>
  request.post<SupplierUserSimpleListItem[]>(
    "/purchase/supplierUser/simpleList",
    data,
  );

// 新增供应商绩效考核信息
export const createSupplierPerformance = (
  data: SavePerformanceEvaluationSchema,
) => request.post<ApiResponse>("/purchase/performance/evaluation/save", data);

/**
 * 更新评估考核建议
 * @param data
 */
export const updatePerformanceEvaluationAdvice = (
  data: UpdatePerformanceEvaluationAdviceSchema,
) =>
  request.post<ApiResponse>(
    "/purchase/performance/evaluation/updateEvaluationAdvice",
    data,
  );

/**
 * 删除供应商绩效考核信息
 * @param id 供应商绩效考核记录id
 */
export const deleteSupplierPerformanceEvaluation = (id: number) =>
  request.post<ApiResponse>(
    `/purchase/performance/evaluation/delete/${id}`,
    {},
  );

/**
 * 获取供应商绩效考核明细信息
 * @param id
 * @returns
 */
export const getSupplierPerformanceEvalutionDetail = (id: number) =>
  request.post<ApiResponse<PerformanceEvaluationDetail>>(
    `/purchase/performance/evaluation/details/${id}`,
    {},
  );

// 获取供应商对应的基础文件列表
export const getSupplierFiles = (supplierUserId: string) =>
  request.get<ApiResponse<SupplierFileListItem[]>>(
    "/purchase/supplier/supplementaryFile/listBySupplierUserId",
    {
      params: {
        supplierUserId,
      },
    },
  );

/**
 * 新增物料信息
 * @param data
 */
export const createSupplierMaterial = (data: CreateSupplierMaterialSchema) =>
  request.post("/purchase/supplier/material/add", data);

/**
 * 编辑物料信息
 * @param data
 */
export const updateSupplierMaterial = (data: UpdateSupplierMaterialSchema) =>
  request.post("/purchase/supplier/material/update", data);

/**
 * 分页获取供应商对应物料信息
 * @param data
 */
export const getSupplierMaterials = (data: QuerySupplierMaterialSchema) =>
  request.post<PageResponse<SupplierMaterialListItem>>(
    "/purchase/supplier/material/page",
    data,
  );

/**
 * 删除物料信息
 * @param id 物料id
 */
export const deleteSupplierMaterial = (id: number) =>
  request.post(`/purchase/supplier/material/delete/${id}`, {});

/**
 * 获取供应商绩效考核指标列表
 */
export const getPurchasePerformanceEvaluationIndicator = () =>
  request.post<ApiResponse<PurchasePerformanceEvaluationIndicator[]>>(
    "/purchase/performance/evaluation/indicator/list",
    {},
  );

/**
 * 保存且发送通知-通知供应商上传文件
 * @param data
 */
export const addAndSend = (data: AddAndSendSupplementaryFileSchema[]) =>
  request.post("/purchase/supplier/supplementaryFile/addAndSend", data);

/**
 * 发送通知提醒
 * @param data
 */
export const sendNotice = (data: number[]) =>
  request.post("/purchase/supplier/supplementaryFile/sendNotification", data);

/**
 * 供应商全部阶段列表
 * @param data
 * @returns
 */
export const queryAllStageSupplierUser = (
  data: QuerySupplierUserAllStageSchema,
) =>
  request.post<PageResponse<AllStageSupplierUserListItem>>(
    "/purchase/supplierUser/allStage",
    data,
  );
/**
 * 获取物料状态列表
 */
export const getMaterialStatusList = () =>
  request.get("/evaluationCategory/materialStatusList");

/**
 * 获取评估流程列表
 * @param materialTypeId 物料类型ID
 * @param deptId 部门ID
 */
export const getEvaluationProcessList = (
  materialTypeId: string,
  deptId: string,
) =>
  request.get(
    `/evaluationCategory/processList?materialTypeId=${materialTypeId}&deptId=${deptId}`,
  );

/**
 * 获取评估分类列表（用于评估邀请中的下拉选）
 * @param id 物料状态ID
 */
export const getEvaluationCategoryList = (id: string) =>
  request.get(`/evaluationCategory/childList?id=${id}`);

/**
 * 提交准入评估申请
 * @param data 评估申请数据
 */
export const submitAssessmentRequest = (data: any) =>
  request.post("/purchase/access/evaluation/submission", data);

/**
 * 获取准入评估列表
 * @param data 请求参数
 */
export const getAccessEvaluationPage = (data: any) =>
  request.post("/purchase/access/evaluation/page", data);

/**
 * 获取准入申请列表
 * @param data 请求参数：pageNum, pageSize, supplierCode, materialCategoryValue, applicationStatus, applicant
 */
export const getAdmissionApplicationPage = (data: any) =>
  request.post("/purchase/admission/application/page", data);

/**
 * 获取准入申请详情
 * @param id 准入申请记录ID
 */
export const getAdmissionApplicationById = (id: string | number) =>
  request.get<ApiResponse>(`/purchase/admission/application/getById/${id}`);

/**
 * 获取准入评估详情
 * @param id 评估记录ID
 */
export const getAccessEvaluationById = (id: string | number) =>
  request.get(`/purchase/access/evaluation/getInfoById/${id}`);

/**
 * 获取准入评估详情（编辑模式）
 * @param id 评估记录ID
 */
export const getAccessEvaluationByEditId = (id: string | number) =>
  request.get(`/purchase/access/evaluation/editInfoById/${id}`);

/**
 * 生成流程单号
 * @param flowCodeEnums 流程编码枚举值
 */
export const generateProcessCode = (flowCodeEnums: string) =>
  request.get("/view/generate/process/code", { params: { flowCodeEnums } });

/**
 * 发送评估邀请
 * @param data 邀请数据
 */
export const sendEvaluationInvitation = (data: any) =>
  request.post("/purchase/access/evaluation/sendInvitation", data);

/**
 * 删除评估邀请
 * @param id 邀请ID
 */
export const deleteEvaluationInvitation = (id: string | number) =>
  request.delete<ApiResponse>(
    `/purchase/access/evaluation/deleteInvitation/${id}`,
  );

/**
 * 获取评估邀请信息
 * @param evaluationId 评估ID
 * @param phaseIds 阶段ID（逗号分隔）
 */
export const getEvaluationInvitation = (
  evaluationId: string | number,
  phaseIds: string,
) =>
  request.get("/purchase/access/evaluation/getEvaluationInvitation", {
    params: {
      evaluationId,
      phaseIds,
    },
  });

/**
 * 审核供应商拓展文件
 * @param data
 * @returns
 */
export const auditSupplierFile = (data: AuditSupplierFileSchema) =>
  request.post("/purchase/supplier/supplementaryFile/audit", data);

/**
 * 新增供应商拓展文件（直接审核通过）
 * @param data
 * @returns
 */
export const saveSupplierFile = (data: SaveSupplementaryFileSchema) =>
  request.post("/purchase/supplier/supplementaryFile/saveFile", data);

/**
 * 修改供应商拓展文件（直接审核通过）
 * @param data
 * @returns
 */
export const updateSupplierFile = (data: UpdateSupplementaryFileSchema) =>
  request.post("/purchase/supplier/supplementaryFile/updateFile", data);

/**
 * 获取供应商的账号列表
 * @param supplierId
 * @returns
 */
export const getSupplierUserAccountList = (supplierId: string) =>
  request.get<ApiResponse<SupplierUserAccountListItem[]>>(
    "/purchase/supplierUser/accountList",
    {
      params: {
        supplierId,
      },
    },
  );

/** 禁用供应商子账号 */
export const disableSupplierSubAccount = (registrationId: number) =>
  request.put(
    "/purchase/supplierUser/accountDisable",
    {},
    {
      params: {
        registrationId,
      },
    },
  );

/** 启用供应商子账号 */
export const enableSupplierSubAccount = (registrationId: number) =>
  request.put(
    "/purchase/supplierUser/accountEnable",
    {},
    {
      params: {
        registrationId,
      },
    },
  );

/** 供应商帐号审核 */
export const auditSupplierAccount = (data: SupplierAccountAuditParams) =>
  request.put("/purchase/supplierUser/accountAudit", data);

/**
 * 获取供应商的关联供应商列表
 * @param supplierId
 * @returns
 */
export const getSupplierRelationList = (supplierId: string) =>
  request.get<ApiResponse<SupplierRelationListItem[]>>(
    "/purchase/supplierUser/relationList",
    {
      params: {
        supplierId,
      },
    },
  );

/**
 * 保存供应商绩效考核指标
 * @param data
 * @returns
 */
export const saveSupplierPerformanceEvaluationIndicator = (
  data: SaveOrUpdateSupplierPerformanceEvaluationIndicatorSchema,
) => request.post("/purchase/performance/evaluation/indicator/save", data);

/**
 * 更新供应商绩效考核指标
 * @param data
 * @returns
 */
export const updateSupplierPerformanceEvaluationIndicator = (
  data: SaveOrUpdateSupplierPerformanceEvaluationIndicatorSchema,
) => request.post("/purchase/performance/evaluation/indicator/update", data);

/**
 * 获取供应商类别对应绩效考核指标列表
 * @param supplierCategoryId
 * @returns
 */
export const getSupplierPerformanceEvaluationIndicatorDetail = (
  supplierCategoryId: string,
) =>
  request.post<ApiResponse<PurchasePerformanceEvaluationIndicator>>(
    `/purchase/performance/evaluation/indicator/category/${supplierCategoryId}`,
    {},
  );

/**
 * 分页查询当前用户的通知公告列表
 * @param data
 * @returns
 */
export const getNoticePageList = (data: QueryNoticePageList) =>
  request.post<PageResponse<NoticeInfoListItem>>(
    "/purchase/notice/page/current",
    data,
  );

/**
 * 物料冻结分页查询
 * @param data
 * @returns
 */
export const getSupplierBlockPageList = (data: any) =>
  request.post<PageResponse<SupplierBlockListItem>>(
    "/purchase/supplier/material/block/page",
    data,
  );

/**
 * 供应商物料冻结信息-根据id查询详情
 * @returns
 */
export const getSupplierBlockDetail = (id: number) =>
  request.get<ApiResponse<SupplierBlockItemDetail>>(
    `/purchase/supplier/material/block/getInfoById/${id}`,
  );

/**
 * 冻结记录根据id删除
 */
export const deleteBlockRecord = (id: number) =>
  request.delete<ApiResponse>(
    `/purchase/supplier/material/block/removeById/${id}`,
  );

/**
 * 供应商黑名单分页查询
 * @param data
 * @returns
 */
export const getSupplierBlacklistPageList = (data: any) =>
  request.post<PageResponse<SupplierBlacklistItem>>(
    "/purchase/supplier/material/blacklist/page",
    data,
  );

/**
 * 供应商黑名单信息-根据id查询详情
 * @returns
 */
export const getSupplierBlacklistDetail = (id: number) =>
  request.get<ApiResponse<SupplierBlacklistItemDetail>>(
    `/purchase/supplier/material/blacklist/getInfoById/${id}`,
  );

/**
 * 供应商黑名单根据id删除
 */
export const deleteblacklistRecord = (id: number) =>
  request.delete<ApiResponse>(
    `/purchase/supplier/material/blacklist/removeById/${id}`,
  );

/**
 * 获取流程历史记录
 * @param insId 流程实例ID
 */
export const getHistoryByInsId = (insId: string | number) =>
  request.get<ApiResponse>(`/view/getHisByIns/${insId}`);

/**
 * 发起会议审批
 * @param data 审批参数
 */
export const startMeetingApproval = (data: any) =>
  request.post<ApiResponse>("/view/start", data);

/**
 * 根据供应商编号及公司获取物料列表
 * @param data
 */
export const getMaterialListBySupplierCode = (data: {
  // 供应商编号
  supplierCode: string;
  // 公司编号列表（工厂）
  factoryValues: string;
}) =>
  request.post<ApiResponse<SupplierCodeMaterialListItem[]>>(
    "/purchase/price/application/getMaterialList",
    {},
    {
      params: data,
    },
  );

/**
 * 审批通过/驳回接口
 * @param data 审批参数
 * @param data.skipType 决策类型：PASS-通过，不传-驳回
 * @param data.message 意见说明
 * @param data.taskId 任务ID（编辑接口返回的taskId）
 * @param data.flowCode 流程编码：ZHUN_RU_PING_GU
 * @param data.variable 流程变量，包含toGm字段表示是否需要GM决策
 */
export const approvalPass = (data: any, taskId: string) =>
  request.post<ApiResponse>("/view/pass?taskId=" + taskId, data);

/**
 * 获取供应商详情
 * @param supplierId 供应商ID
 * @param audit 审核标记（传false）
 * @returns 供应商详情信息
 */
export const getSupplierDetail = (supplierId: string | number) =>
  request.get<ApiResponse>("/purchase/supplierRegister/supplierDetail", {
    params: {
      supplierId,
      audit: false,
    },
  });

/**
 * 价格申请分页查询
 * @param data 请求参数：pageNum, pageSize, supplierCode, supplierName, materialCode, materialName, applicant, applicationStatus, factoryValue
 */
export const getPriceApplicationPage = (data: any) =>
  request.post("/purchase/price/application/page", data);

/**
 * 获取供应商用户信息
 * @param supplierCode 供应商编号
 * @param currentStage 当前阶段，默认值3
 */
export const getSupplierUserInfo = (
  supplierCode: string,
  currentStage: number = 3,
) =>
  request.get<ApiResponse>("/purchase/supplierUser/getSupplierUserInfo", {
    params: {
      supplierCode,
      currentStage,
    },
  });
